export const deepClone = <T>(data: T): T => JSON.parse(JSON.stringify(data))

export const isEqual = <T>(x: T, y: T): boolean => {
  if (Object.is(x, y)) {
    return true
  }

  // Depending on object structure, checks for Date & RegEx can be removed
  if (x instanceof Date && y instanceof Date) {
    return x.getTime() === y.getTime()
  }
  if (x instanceof RegExp && y instanceof RegExp) {
    return x.toString() === y.toString()
  }

  // For non-objects or null, use strict equality
  if (
    typeof x !== 'object' ||
    x === null ||
    typeof y !== 'object' ||
    y === null
  ) {
    return x === y
  }

  // Compare objects
  const keys = Object.keys(x) as (keyof T)[]
  return (
    keys.length === Object.keys(y).length &&
    keys.every((key) => Reflect.has(y, key) && isEqual(x[key], y[key]))
  )
}

/**
 * Groups elements of an array by a key derived from a callback function.
 * This is a polyfill for Object.groupBy which is not available in Safari < 16.4.
 *
 * @param array - The array to group
 * @param callbackfn - A function that returns the key to group by
 * @returns An object with keys as the result of the callback and values as arrays of items
 */
export function groupBy<T>(
  array: readonly T[],
  callbackfn: (element: T, index: number) => PropertyKey,
): Partial<Record<PropertyKey, T[]>> {
  const result: Partial<Record<PropertyKey, T[]>> = {}

  for (let i = 0; i < array.length; i++) {
    const key = callbackfn(array[i], i)
    if (!result[key]) {
      result[key] = []
    }
    result[key]!.push(array[i])
  }

  return result
}
